import { useEffect, useState } from 'react'
import { useQueries } from '@tanstack/react-query'

import Loader from 'common/components/loaders/Loader'
import { useAppDispatch, useAppSelector } from 'common/hooks/redux'
import { getVariableCombinations } from 'features/unifiedFlow/api'
import { isComponentElement } from 'features/unifiedFlow/api/interfaces'
import { contentKeys } from 'features/unifiedFlow/api/queryKeys'
import { getExperimentBadgesState } from 'features/unifiedFlow/contentPage/helpers'
import { selectElement } from 'features/unifiedFlow/store/unifiedFlowSlice'

import ElementCard from '../../components/ElementCard'
import ExperimentTypeBadges from '../../components/ExperimentTypeBadges'
import ElementCardPersonalize from '../../components/personalizeCard/ElementCardPersonalize'
import { useContent, useSelectedComponentElement } from '../../hooks'
import useVariantsToApproveQuery from '../../hooks/useVariantsToApproveQuery'
import { useVariantLanguageContext } from '../context/VariantLanguageContext'

type Props = {
  onSelectPersonalization: (personalizationId: string | undefined) => void
  selectedPersonalizationId: string | undefined
  statusFilter?: string
}

const ElementsList = ({
  selectedPersonalizationId,
  onSelectPersonalization,
  statusFilter,
}: Props) => {
  const dispatch = useAppDispatch()
  const { clearVariant: clearSelectedVariant } = useVariantLanguageContext()
  const [expandedElementId, setExpandedElementId] =
    useState<number | undefined>(undefined)

  const selectedElementId = useAppSelector(
    (state) => state.unifiedFlow.selectedElementId
  )

  useEffect(() => {
    if (statusFilter !== 'pending') {
      setExpandedElementId(undefined)
      onSelectPersonalization(undefined)
    }
  }, [statusFilter, onSelectPersonalization])

  useVariantsToApproveQuery()

  const accountId = useAppSelector((state) => state.authStates.accountId)
  const { content } = useContent()

  const elements = content?.elements
  const componentElements = elements?.filter(isComponentElement) ?? []
  const { data: selectedElement } = useSelectedComponentElement()

  const queries =
    elements
      ?.filter(isComponentElement)
      .filter((element) => !!element.personalization_id)
      .map((element) => {
        const currentPersonalization =
          element.personalization_id === selectedElement?.personalization_id
            ? element
            : undefined

        const region =
          currentPersonalization?.personalization_settings?.configuration
            .privacy_region

        return {
          queryKey: contentKeys.variableCombinations({
            personalizationId: element?.personalization_id ?? '',
            region,
            accountId,
          }),
          queryFn: () =>
            getVariableCombinations({
              accountId,
              personalizationId: element!.personalization_id!,
              region: region,
            }),
          enabled: !!element?.personalization_id && !!region,
        }
      }) ?? []

  const results = useQueries({
    queries,
  })

  return (
    <Loader
      className="m-auto"
      hasWrapperFullHeight
      isLoading={results.some((result) => result.isInitialLoading)}
    >
      <div
        role="tablist"
        data-testid="elementsContainer"
        className="flex flex-col gap-4"
      >
        {componentElements.map((element) => {
          const { element_id, name, display_name, icon, status } = element
          const badgesState = getExperimentBadgesState(element)

          const isElementDisabled = status === 'disabled'

          const hasError = isComponentElement(element)
            ? element.nlg_status === 'error'
            : undefined

          const commonProps = {
            key: element_id,
            element,
            title: display_name || name,
            icon,
            content: badgesState && <ExperimentTypeBadges {...badgesState} />,
          }

          const commonPropsComponentElement = {
            ...commonProps,
            isDisabled: isElementDisabled,
            hasError,
            isSelected: element_id === selectedElementId && !isElementDisabled,
            onClick: () => {
              if (selectedElementId !== element_id) {
                onSelectPersonalization(undefined)
                setExpandedElementId(undefined)
              }

              if (selectedPersonalizationId) {
                onSelectPersonalization(undefined)
              }

              if (!isElementDisabled) {
                dispatch(selectElement({ elementId: element_id }))
                clearSelectedVariant()
              }
            },
          }

          if (isComponentElement(element)) {
            const personalizedElements = componentElements.filter(
              (el) => !!el.personalization_id
            )
            const personalizedIndex = personalizedElements.findIndex(
              (elt) => elt.element_id === element_id
            )

            return badgesState?.isPersonalizable ? (
              <ElementCardPersonalize
                {...commonPropsComponentElement}
                selectedPersonalizationId={selectedPersonalizationId}
                onClickPersonalizationId={onSelectPersonalization}
                variableCombinations={
                  results[personalizedIndex]?.data?.items ?? []
                }
                expandedElementId={expandedElementId}
                onSetExpandedElementId={(elementId) =>
                  setExpandedElementId(elementId)
                }
                elements={componentElements}
                statusFilter={statusFilter}
              />
            ) : (
              <ElementCard {...commonPropsComponentElement} />
            )
          }

          return null
        })}
      </div>
    </Loader>
  )
}

export default ElementsList
